<template>
  <div class="row">
    <div class="col-12">
      <div class="card">
        <!-- 헤더 -->
        <DetailHeader :headerTitle="headerTitle" :backTo="'RawMaterials'"/>

        <!-- 바디 -->
        <div class="pt-3 pb-4 d-flex justify-content-center">
          <div v-if="isEdit && loading">
            <TableBodyLoader :minHeight="715" :offsetY="-50" message="상세정보를 불러오는 중..." />
          </div>
          <div v-else class="w-100 px-5">

            <div class="row gx-4">
              <div class="col-lg-8 col-md-7" data-tour="dcreate.left">
                <section class="card border-0 h-100">
                  <div class="card-body">
                    <h6 class="mb-3">상세정보</h6>
                    <!-- 썸네일 -->
                    <ImagePicker
                      class="mb-3"
                      v-model:file="form.imgFile"
                      :existing-preview="form.imgPreview"
                      :existing-name="form.imgFilename"
                      :max-size="10 * 1024 * 1024"
                      label="대표 이미지"
                      alt="scanplace"
                      @selected="onImgSelected"
                      @cleared="onImgCleared"
                      @error="toastError"
                    />
                      <!-- 필드 -->
                      <div class="row g-3">
                        <div class="col-md-6">
                          <label class="form-label">재료 분류 <span class="text-danger">*</span></label>
                          <select class="form-select" v-model="form.type">
                            <option value="" disabled>분류를 선택하세요</option>
                            <option v-for="c in categoryList" :key="c.id" :value="c.categoryCode">
                              {{ c.categoryName }} ({{ c.categoryCode }})
                            </option>
                          </select>
                        </div>

                        <div class="col-md-6">
                          <label class="form-label">재료 이름 <span class="text-danger">*</span></label>
                          <input type="text" class="form-control" v-model.trim="form.name" placeholder="예: 스테인리스강 304" />
                        </div>

                        <div class="col-md-6">
                          <label class="form-label">URN <span class="text-danger">*</span></label>
                          <input type="text" class="form-control" v-model.trim="form.urn" placeholder="예: urn:epc:id:..." />
                        </div>

                        <div class="col-md-6">
                          <label class="form-label">원산지 국가</label>
                          <multiselect
                            v-model="form.originCountry"
                            class="ms-country"
                            :options="countryOptions"
                            track-by="code"
                            label="name"
                            placeholder="국가 선택"
                            select-label="선택" 
                            selected-label="선택"
                            deselect-label="해제"
                            :allow-empty="true"
                            :searchable="true"
                          >
                          </multiselect>
                        </div>

                        <div class="col-md-6">
                          <label class="form-label">유해성 여부</label>
                          <select class="form-select" v-model="form.hazYn">
                            <option value="Y">예</option>
                            <option value="N">아니오</option>
                          </select>
                        </div>

                        <div class="col-md-6">
                          <label class="form-label">친환경 여부</label>
                          <select class="form-select" v-model="form.ecoYn">
                            <option value="N">예</option>
                            <option value="Y">아니오</option>
                          </select>
                        </div>

                        <div class="col-12">
                          <label class="form-label">설명</label>
                          <textarea class="form-control" rows="3" v-model.trim="form.dsc" placeholder="재료 설명을 입력하세요."></textarea>
                        </div>
                      </div>

                      <!-- 메타 -->
                      <div class="row g-3 mt-1 mb-3" v-if="isEdit">
                        <div class="col-md-6">
                          <label class="form-label">생성일시</label>
                          <input type="text" class="form-control" :value="formatDate(form.createdAt) || '-'" disabled />
                        </div>
                        <div class="col-md-6">
                          <label class="form-label">수정일시</label>
                          <input type="text" class="form-control" :value="formatDate(form.updatedAt) || '-'" disabled />
                        </div>
                      </div>
                  </div>
                </section>
              </div>

              <!-- ▼▼ 오른쪽: 탄소배출량 슬라이더 ▼▼ -->
              <div class="col-lg-4 col-md-5 mt-3 mt-md-0 d-flex flex-column" data-tour="dcreate.co2">
                <co2-slider
                  v-model="carbonEmissionLocal"
                  :min="emissionMin"
                  :max="emissionMax"
                  :step="emissionStep"
                  title="탄소배출량"
                  hint="드래그로 탄소 배출량을 조절하세요."
                  :autoExpandMax="true"
                  :expandUnit="50"
                />
                <co2-info :rangeText="'원재료의 생산'"/>
              </div>

            </div> <!-- /row -->

            <!-- 액션 -->
            <div class="d-flex justify-content-between mt-5 mb-3 gap-2 px-2">
              <soft-button color="outline-secondary" @click="$router.push({ name: 'RawMaterials', query: { ...$route.query } })">
                목록
              </soft-button>
              <div class="d-flex gap-2">
                <soft-button v-if="isEdit" color="danger" @click="remove" :disabled="loading">삭제</soft-button>
                <soft-button color="success" @click="save" :disabled="loading" data-tour="dcreate.save">저장</soft-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <RawMaterialTour />
  <SoftConfirm ref="confirm" />
</template>


<script>
import SoftConfirm from "@/components/SoftConfirm.vue";
import DetailHeader from "@/components/DetailHeader.vue";
import SoftButton from "@/components/SoftButton.vue";
import TableBodyLoader from "@/components/loading/TableBodyLoader.vue";
import ImagePicker from "@/views/components/common/ImagePicker.vue";
import Co2Info from "@/views/components/co2/Co2Info.vue";
import Co2Slider from "@/views/components/co2/Co2Slider.vue";
import Multiselect from 'vue-multiselect'
import countries from "i18n-iso-countries";
import ko from "i18n-iso-countries/langs/ko.json";  // 한국어 국가명
import en from "i18n-iso-countries/langs/en.json";  // fallback
import RawMaterialTour from "@/components/tour/RawMaterialTour.vue";
import api from "@/api/api";

countries.registerLocale(ko);
countries.registerLocale(en);

export default {
  name: "MaterialDetail",
  components: { SoftConfirm, DetailHeader, SoftButton, TableBodyLoader, ImagePicker, Multiselect, Co2Info, Co2Slider, RawMaterialTour },
  inject: ['showLoading', 'hideLoading'],
  props: {
    mode: { type: String, default: "create" },
    id: { type: [String, Number], default: "" }
  },
  data() {
    return {
      loading: false,
      form: {
        id: null,
        type: "",
        name: "",
        dsc: "",
        originCountry: null,
        hazYn: "N",
        ecoYn: "N",
        urn: "",
        iconName: "",
        mfId: "",
        atchFileId: "",
        imgFile: null,
        imgPreview: "",
        imgFilename: "",
        createdAt: "",
        updatedAt: "",
        carbonEmission: "0" 
      },
      categoryList: [],
      imgChanged: false,
      imgRemoved: false, 
        
      emissionMin: 0,
      emissionMax: 50,
      emissionStep: 0.01,
      carbonEmissionLocal: 0,

      countryOptions: [],
    };
  },
  computed: {
    isEdit() { return this.mode === "edit" && !!this.id; },
    headerTitle() { return this.isEdit ? "재료정보 수정" : "재료정보 등록"; },
    displayFileName() {
      return this.form.imgFile?.name || this.form.imgFilename || "";
    },
  },
  watch: {
    'form.carbonEmission'(val) {
      const num = Number(val);
      if (!Number.isNaN(num)) {
        const fixed = Number(num.toFixed(2));
        if (fixed !== this.carbonEmissionLocal) this.carbonEmissionLocal = fixed;
        if (fixed > this.emissionMax) this.emissionMax = Math.ceil(fixed / 50) * 50;
      }
    },
    carbonEmissionLocal(val) {
      const fixedStr = Number(val).toFixed(2);
      if (fixedStr !== this.form.carbonEmission) this.form.carbonEmission = fixedStr;
    },
  },
  created() {
    const names = countries.getNames("ko", { select: "official" }) 
               || countries.getNames("en");
    this.countryOptions = Object.entries(names)
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "ko"));
  },
  async mounted() {
    this.form.mfId = this.$store.getters['UserStore/getMfId'];
    this.initData();
    
    if (this.isEdit) await this.fetchDetail();
    else this.prefillUrnIfCreate();

    const v = Number(this.form.carbonEmission || 0);
    this.carbonEmissionLocal = Number.isFinite(v) ? Number(v.toFixed(2)) : 0;
    if (this.carbonEmissionLocal > this.emissionMax) {
      this.emissionMax = Math.ceil(this.carbonEmissionLocal / 50) * 50;
    }
    
    this._tourCountryHandler = (e) => {
      const { code, name } = e.detail || {};
      // code 또는 name으로 매칭
      const match =
        this.countryOptions.find(o => (code && o.code === code.toUpperCase()))
        || this.countryOptions.find(o => (name && o.name === name))
        || null;
      this.form.originCountry = match;
    };
    window.addEventListener('tour:setCountry', this._tourCountryHandler);

    this._tourImgHandler = (e) => {
      const { file, blob, name } = e.detail || {};
      if (!file || !blob) return;

      if (this.form.imgPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(this.form.imgPreview);
      }
      const previewUrl = URL.createObjectURL(blob);

      this.form.imgFile     = file;
      this.form.imgPreview  = previewUrl;
      this.form.imgFilename = name || file.name || 'demo.jpg';

      this.imgChanged = true;
      this.imgRemoved = false;
    };
    window.addEventListener('tour:setImage', this._tourImgHandler);
  },
  beforeUnmount() {
    if (this.form.imgPreview) URL.revokeObjectURL(this.form.imgPreview);
    
    window.removeEventListener('tour:setCountry', this._tourCountryHandler);

    window.removeEventListener('tour:setImage', this._tourImgHandler);
    if (this.form.imgPreview?.startsWith('blob:')) URL.revokeObjectURL(this.form.imgPreview);
  },
  methods: {
    prefillUrnIfCreate() {
      if (this.form.urn && this.form.urn.trim()) return;
      const mfInfo = this.$store.getters['UserStore/getMfInfo'];
      const companyPrefix = mfInfo?.companyPrefix
        ?? this.$store.getters['UserStore/getMfId'] // 혹시 여기서도 제공한다고 하셨으니 백업
        ?? '';
      if (!companyPrefix) return; 

      this.form.urn = `urn:epc:id:sgtin:${companyPrefix}.`;
    },
    async initData() {
      try {
        const mfId = this.$store.getters['UserStore/getMfId'];
        const { data } = await api.get(`/rawMaterial/initData`, { params: { mfId } });
        const result = data?.result || {};

        this.categoryList = result.categoryList || [];
      } catch (error) {
        console.error("초기 데이터 불러오기 실패:", error);
      }
    },
    async fetchDetail() {
      this.loading = true;
      try {
        const [res] = await Promise.all([
          api.get(`/rawMaterial`, { params: { id: this.id } }),
          new Promise(r => setTimeout(r, 300))
        ]);
        const item = res.data?.result?.rawMaterial || {};
        const files = res.data?.result?.files || [];
        Object.assign(this.form, item);
        
        if (files.length > 0) {
          this.form.imgPreview  = files[0].downloadUrl || "";
          this.form.imgFilename = files[0].originalName || "";
        } else {
          this.form.imgPreview  = "";
          this.form.imgFilename = "";
        }
        this.files = files;

        this.carbonEmissionLocal = Number(this.form.carbonEmission || 0) || 0;
        if (this.carbonEmissionLocal > this.emissionMax) {
          this.emissionMax = Math.ceil(this.carbonEmissionLocal / 50) * 50;
        }
        
        let code = this.form.originCountry;
        if (code && typeof code === 'object') code = code.code;
        if (typeof code === 'string') code = code.toUpperCase();

        const matched = this.countryOptions.find(o => o.code === code) || null;
        this.form.originCountry = matched;
      } catch (e) {
        this.toastError?.("상세 조회 중 오류가 발생했습니다.");
      } finally {
        this.loading = false;
      }
    },
    onImgSelected({ file, previewUrl, name }) {
      if (this.form.imgPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(this.form.imgPreview);
      }
      this.form.imgFile     = file;
      this.form.imgPreview  = previewUrl;
      this.form.imgFilename = name;

      this.imgChanged = true;
      this.imgRemoved = false;
    },
    onImgCleared() {
      if (this.form.imgPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(this.form.imgPreview);
      }
      this.form.imgFile     = null;
      this.form.imgPreview  = "";
      this.form.imgFilename = "";

      this.imgChanged = false;
      this.imgRemoved = true;
    },
    buildFormData() {
      const fd = new FormData();
      fd.append("type", this.form.type ?? "");
      fd.append("name", this.form.name ?? "");
      if (this.form.dsc)            fd.append("dsc", this.form.dsc);
      if (this.form.hazYn)         fd.append("hazYn", this.form.hazYn);
      if (this.form.ecoYn)         fd.append("ecoYn", this.form.ecoYn);
      if (this.form.urn)            fd.append("urn", this.form.urn);
      if (this.form.iconName)      fd.append("iconName", this.form.iconName);
      if (this.form.mfId !== null && this.form.mfId !== undefined) fd.append("mfId", String(this.form.mfId));
      if (this.form.carbonEmission)  fd.append("carbonEmission", Number(this.form.carbonEmission || 0).toFixed(2));

      const origin = this.form.originCountry?.code;
      if (origin) fd.append("originCountry", origin.toUpperCase());

      if (this.form.atchFileId) fd.append("atchFileId", this.form.atchFileId);
      fd.append("imgChanged", this.imgChanged ? "Y" : "N");
      fd.append("imgRemoved", this.imgRemoved ? "Y" : "N");
      if (this.form.imgFile) fd.append("file", this.form.imgFile);
      return fd;
    },
    async save() {
      if (!this.form.type?.trim()) { this.toastError?.("재료 분류를 입력하세요."); return; }
      if (!this.form.name?.trim()) { this.toastError?.("재료 이름을 입력하세요."); return; }

      const action = this.isEdit ? '수정' : '등록';
      const ok = await this.$refs.confirm.open({
        title: `재료정보 ${action}`,
        message: `${action} 하시겠습니까?`,
        confirmText: action,
        cancelText: "취소",
        confirmColor: "success",
      });
      if (!ok) return;

      const delay = (ms) => new Promise(r => setTimeout(r, ms));
      const started = performance.now();

      this.showLoading?.('저장중...');
      try {
        const fd = this.buildFormData();
        
        if (this.isEdit) {
          await api.put(`/rawMaterial/${this.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } else {
          const { data } = await api.post(`/rawMaterial`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          this.form.id = data?.result?.id;
        }

        const elapsed = performance.now() - started;
        if (elapsed < 1000) await delay(1000 - elapsed);

        this.toastSuccess?.(`재료정보가 ${action}되었습니다.`);
        this.$router.push({
          name: "RawMaterialEdit",
          params: { id: this.form.id },
          query: { ...this.$route.query, t: Date.now()},
        });

      } catch (e) {
        console.log(e)
        this.toastError?.(`${action} 중 오류가 발생했습니다.`);
      } finally {
        this.hideLoading?.();
      }
    },

    async remove() {
      if (!this.isEdit) return;

      const ok = await this.$refs.confirm.open({
        title: "삭제 확인",
        message: "정말 삭제하시겠습니까?",
        confirmText: "삭제",
        cancelText: "취소",
        confirmColor: "danger",
      });
      if (!ok) return;
  
      this.showLoading?.('삭제중...');
      try {
        const delay = ms => new Promise(r => setTimeout(r, ms));
        await Promise.all([
          api.delete(`/rawMaterial/${this.id}`),
          delay(500),
        ]);

        this.toastSuccess?.("삭제되었습니다.");
        this.$router.push({
          name: "RawMaterials",
          query: { ...this.$route.query }, 
        });
      } catch (e) {
        this.toastError?.("삭제 중 오류가 발생했습니다.");
      } finally {
        this.hideLoading?.();
      }
    }
  }
};
</script>

<style scoped>
@import "~vue-multiselect/dist/vue-multiselect.css";
::v-deep .ms-country .multiselect__option { font-size: 13px; }
::v-deep .ms-country .multiselect__single {font-size: 13px;}
::v-deep .ms-country .multiselect__placeholder {font-size: 13px;}


.card { border: 1px solid rgba(0,0,0,.06) !important; }

</style>
